
import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Input, Badge } from '../components/UI';
import { useLeague } from '../components/LeagueContext';
import { User, UserRole } from '../types';
import { Shield, UserPlus, Search, Edit3, Save, X, Settings, Users, Loader2, Palette, Upload, ImageIcon, ShieldCheck, ListOrdered, Database, TestTube, Play, Trash2, Ban, Unlock, Activity, BookOpen, Crown, Car, MessageSquare, Send, MessageCircle, Clock, Zap, Power, Archive, UserCheck, UserX, GitMerge, UserMinus } from 'lucide-react';
import { apiRequest } from '../utils/api';
import { API_BASE_URL } from '../config';
import { useAuth } from '../components/AuthContext';

// ... (UserManager and PointsEditor components remain mostly unchanged, just ensure they are included in the final file)

// Re-including UserManager component for completeness of file
const UserManager: React.FC = () => {
    const { drivers: users, settings, refreshData } = useLeague();
    const [searchTerm, setSearchTerm] = useState('');
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [isCreatingPlaceholder, setIsCreatingPlaceholder] = useState(false);
    const [mergingUser, setMergingUser] = useState<User | null>(null);
    const [mergeTargetId, setMergeTargetId] = useState('');
    const [newUser, setNewUser] = useState({ username: '', password: '', eaId: '' });
    const [newPlaceholder, setNewPlaceholder] = useState({ username: '', eaId: '' });
    const [seeding, setSeeding] = useState(false);

    const filteredUsers = users.filter(u => 
        u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (u.eaId && u.eaId?.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const getPriority = (u: any) => {
        if (!u.is_active) return 10; // Inactive at bottom
        if (u.roles.includes(UserRole.ADMIN) || u.roles.includes(UserRole.WEBADMIN)) return 1;
        if (u.roles.includes(UserRole.STEWARD)) return 2;
        if (u.teamId && !u.is_reserve) return 3; 
        if (u.teamId && u.is_reserve) return 4; 
        if (u.is_reserve) return 5; 
        return 6; 
    };

    const sortedUsers = [...filteredUsers].sort((a: any, b: any) => {
        const pA = getPriority(a);
        const pB = getPriority(b);
        if (pA !== pB) return pA - pB;
        return a.username.localeCompare(b.username);
    });

    const handleRoleToggle = (role: UserRole) => {
        if (!editingUser) return;
        const currentRoles = editingUser.roles;
        let newRoles;
        if (currentRoles.includes(role)) {
            newRoles = currentRoles.filter(r => r !== role);
        } else {
            newRoles = [...currentRoles, role];
        }
        setEditingUser({ ...editingUser, roles: newRoles });
    };

    const toggleTelemetryAccess = () => {
        if (!editingUser) return;
        setEditingUser({ ...editingUser, has_telemetry_access: !editingUser.has_telemetry_access });
    };

    const saveUser = async () => {
        if (!editingUser) return;
        try {
            await apiRequest('users.php', 'PUT', {
                id: editingUser.id,
                username: editingUser.username,
                eaId: editingUser.eaId,
                discord_id: editingUser.discord_id,
                roles: editingUser.roles,
                has_telemetry_access: editingUser.has_telemetry_access
            });
            await refreshData();
            setEditingUser(null);
        } catch (e: any) {
            alert('Fehler beim Speichern: ' + e.message);
        }
    };

    const createUser = async () => {
        try {
            await apiRequest('users.php', 'POST', newUser);
            await refreshData();
            setIsCreating(false);
            setNewUser({ username: '', password: '', eaId: '' });
        } catch (e: any) {
            alert('Fehler beim Erstellen: ' + e.message);
        }
    };

    const createPlaceholder = async () => {
        try {
            await apiRequest('users.php', 'POST', { ...newPlaceholder, action: 'create_placeholder' });
            await refreshData();
            setIsCreatingPlaceholder(false);
            setNewPlaceholder({ username: '', eaId: '' });
        } catch (e: any) {
            alert('Fehler beim Erstellen: ' + e.message);
        }
    };

    const handleMerge = async () => {
        if (!mergingUser || !mergeTargetId) return;
        const target = users.find(u => u.id === mergeTargetId);
        if (!target) return;

        if (!confirm(`Möchtest du '${mergingUser.username}' wirklich in '${target.username}' MERGEN?\n\nAlle Ergebnisse, Tickets und Saisons von '${mergingUser.username}' werden auf '${target.username}' übertragen.\n\nDer Platzhalter '${mergingUser.username}' wird danach gelöscht.`)) return;

        try {
            await apiRequest('users.php', 'POST', { 
                action: 'merge_users', 
                placeholderId: mergingUser.id, 
                targetUserId: mergeTargetId 
            });
            await refreshData();
            setMergingUser(null);
            setMergeTargetId('');
        } catch (e: any) {
            alert("Fehler beim Mergen: " + e.message);
        }
    };

    const handleSeedDrivers = async () => {
        if (!confirm("Dies erstellt 20 offizielle F1-Fahrer als Test-Accounts. Passwort für alle ist 'test1234'. Fortfahren?")) return;
        setSeeding(true);
        try {
            const res = await apiRequest<{message: string}>('users.php', 'POST', { action: 'seed_drivers' });
            alert(res.message);
            await refreshData();
        } catch (e: any) {
            alert("Fehler: " + e.message);
        } finally {
            setSeeding(false);
        }
    };

    const handleDeleteUser = async (user: User) => {
        if (!confirm(`WARNUNG: Möchtest du '${user.username}' wirklich LÖSCHEN?\n\nDies entfernt ALLE Daten (Ergebnisse, Tickets, Historie) unwiderruflich!\n\nNutze lieber 'Deaktivieren', wenn der Fahrer die Liga nur verlassen hat.`)) return;
        if (!confirm(`Sicherheitsprüfung: Bestätige die endgültige Löschung von '${user.username}'.`)) return;
        
        try {
            await apiRequest(`users.php?id=${user.id}`, 'DELETE');
            await refreshData();
        } catch (e: any) {
            alert("Fehler beim Löschen: " + e.message);
        }
    };

    const handleToggleStatus = async (user: User) => {
        const newStatus = !user.is_active;
        const action = newStatus ? "reaktivieren" : "archivieren/deaktivieren";
        
        if (!confirm(`Möchtest du '${user.username}' wirklich ${action}?\n\n${newStatus ? 'Der User kann sich wieder einloggen.' : 'Der User kann sich NICHT mehr einloggen. Daten (Strafen/Ergebnisse) bleiben erhalten.'}`)) return;

        try {
            await apiRequest('users.php', 'PUT', {
                id: user.id,
                is_active: newStatus
            });
            await refreshData();
        } catch (e: any) {
            alert("Fehler: " + e.message);
        }
    };

    const handleResetPassword = async (user: User) => {
        if (!confirm(`Passwort von '${user.username}' zurücksetzen?\n\nDas neue Standard-Passwort wird angezeigt.`)) return;
        try {
            const res = await apiRequest<{message: string; temporaryPassword?: string}>('users.php', 'PUT', {
                id: user.id,
                action: 'reset_password'
            });
            alert(res.temporaryPassword
                ? `${res.message}\n\nTemporäres Passwort: ${res.temporaryPassword}\n\nBitte sicher an den Fahrer übermitteln.`
                : res.message
            );
        } catch (e: any) {
            alert("Fehler: " + e.message);
        }
    };

    const getRoleLabel = (role: string) => {
        if (role === UserRole.ADMIN) return 'LEITUNG';
        if (role === UserRole.WEBADMIN) return 'DEV';
        if (role === UserRole.STEWARD) return 'STEWARD';
        if (role === UserRole.DRIVER) return 'DRIVER';
        return role;
    };

    const getImageUrl = (url?: string | null) => {
        if (!url) return undefined;
        if (url.startsWith('http') || url.startsWith('data:')) return url;
        const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
        const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
        const cleanUrl = url.startsWith('/') ? url.slice(1) : url;
        return cleanBase ? `${cleanBase}/${cleanUrl}` : cleanUrl;
    };

    const rulebookTimestamp = settings.rulebook_updated_at ? new Date(settings.rulebook_updated_at).getTime() : 0;

    return (
        <div className="space-y-6">
             <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="relative flex-1 w-full md:max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input 
                        className="w-full bg-gray-900 border border-gray-700 rounded py-2 pl-10 pr-3 text-sm focus:border-f1-red outline-none"
                        placeholder="Benutzer suchen..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="flex gap-2 w-full md:w-auto">
                    <Button variant="secondary" icon={UserMinus} onClick={() => setIsCreatingPlaceholder(true)}>Platzhalter</Button>
                    <Button icon={UserPlus} onClick={() => setIsCreating(true)}>Neuer Benutzer</Button>
                </div>
            </div>

            {isCreatingPlaceholder && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
                    <Card className="w-full max-w-md border-gray-600 bg-f1-dark">
                        <h3 className="text-xl font-bold mb-4">Platzhalter-Fahrer anlegen</h3>
                        <p className="text-sm text-gray-400 mb-4">Ideal für historische Saisons oder Fahrer ohne Account. Kann später mit einem echten Account zusammengeführt werden.</p>
                        <div className="space-y-3">
                            <Input label="Anzeigename (z.B. Sebastian Vettel)" value={newPlaceholder.username} onChange={e => setNewPlaceholder({...newPlaceholder, username: e.target.value})} />
                            <Input label="EA ID (Optional)" value={newPlaceholder.eaId} onChange={e => setNewPlaceholder({...newPlaceholder, eaId: e.target.value})} />
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <Button variant="ghost" onClick={() => setIsCreatingPlaceholder(false)}>Abbrechen</Button>
                            <Button onClick={createPlaceholder}>Platzhalter erstellen</Button>
                        </div>
                    </Card>
                </div>
            )}

            {mergingUser && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
                    <Card className="w-full max-w-md border-gray-600 bg-f1-dark">
                        <h3 className="text-xl font-bold mb-4">Fahrer zusammenführen</h3>
                        <p className="text-sm text-gray-400 mb-4">Übertrage alle Daten von <b>{mergingUser.username}</b> auf einen anderen Account.</p>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-gray-400 text-sm font-bold mb-2 uppercase">Ziel-Account auswählen</label>
                                <select 
                                    className="w-full bg-gray-900 border border-gray-700 rounded py-2 px-3 text-white outline-none focus:border-f1-red"
                                    value={mergeTargetId}
                                    onChange={e => setMergeTargetId(e.target.value)}
                                >
                                    <option value="">-- Ziel wählen --</option>
                                    {users.filter(u => u.id !== mergingUser.id && !u.is_placeholder).map(u => (
                                        <option key={u.id} value={u.id}>{u.username} {u.eaId ? `(${u.eaId})` : ''}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 mt-6">
                            <Button variant="ghost" onClick={() => setMergingUser(null)}>Abbrechen</Button>
                            <Button variant="primary" icon={GitMerge} onClick={handleMerge} disabled={!mergeTargetId}>Merge bestätigen</Button>
                        </div>
                    </Card>
                </div>
            )}

            {isCreating && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
                    <Card className="w-full max-w-md border-gray-600 bg-f1-dark">
                        <h3 className="text-xl font-bold mb-4">Neuen Benutzer registrieren</h3>
                        <div className="space-y-3">
                            <Input label="Benutzername" value={newUser.username} onChange={e => setNewUser({...newUser, username: e.target.value})} />
                            <Input label="Start-Passwort" type="password" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} />
                            <Input label="EA ID (Optional)" value={newUser.eaId} onChange={e => setNewUser({...newUser, eaId: e.target.value})} />
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <Button variant="ghost" onClick={() => setIsCreating(false)}>Abbrechen</Button>
                            <Button onClick={createUser}>Account erstellen</Button>
                        </div>
                    </Card>
                </div>
            )}

            {editingUser && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
                    <Card className="w-full max-w-lg border-gray-600 bg-f1-dark">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold">Benutzer bearbeiten</h3>
                            <button onClick={() => setEditingUser(null)}><X className="text-gray-400 hover:text-white"/></button>
                        </div>
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 gap-4">
                                <Input label="Benutzername" value={editingUser.username || ''} onChange={e => setEditingUser({...editingUser, username: e.target.value})} />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Input label="EA ID / Gamertag" value={editingUser.eaId || ''} onChange={e => setEditingUser({...editingUser, eaId: e.target.value})} />
                                <div>
                                    <label className="block text-gray-400 text-sm font-bold mb-2 uppercase">Discord User ID</label>
                                    <input 
                                        className="w-full bg-gray-900 border border-gray-700 rounded py-2 px-3 text-white font-mono focus:border-f1-red outline-none transition-colors"
                                        placeholder="123456789012345678"
                                        value={editingUser.discord_id || ''}
                                        onChange={e => setEditingUser({...editingUser, discord_id: e.target.value})}
                                    />
                                </div>
                            </div>
                            
                            {/* ROLES */}
                            <div>
                                <label className="block text-gray-400 text-sm font-bold mb-3 uppercase">Zugewiesene Rollen</label>
                                <div className="grid grid-cols-2 gap-3">
                                    {Object.values(UserRole).map(role => {
                                        const isActive = editingUser.roles.includes(role);
                                        return (
                                            <div key={role} onClick={() => handleRoleToggle(role)} className={`p-3 rounded border cursor-pointer flex items-center justify-between transition-colors ${isActive ? 'bg-f1-red/20 border-f1-red text-white' : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'}`}>
                                                <span className="text-sm font-bold">{getRoleLabel(role)}</span>
                                                {isActive && <div className="w-2 h-2 rounded-full bg-f1-red shadow-[0_0_8px_rgba(225,6,0,0.8)]"></div>}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* SPECIAL PERMISSIONS */}
                            <div className="pt-4 border-t border-gray-700">
                                <label className="block text-gray-400 text-sm font-bold mb-3 uppercase">Spezial-Zugriff</label>
                                <div onClick={toggleTelemetryAccess} className={`p-3 rounded border cursor-pointer flex items-center justify-between transition-colors ${editingUser.has_telemetry_access ? 'bg-blue-900/30 border-blue-500 text-white' : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'}`}>
                                    <div className="flex items-center gap-3">
                                        <Activity size={18} className={editingUser.has_telemetry_access ? 'text-blue-400' : ''}/>
                                        <span className="text-sm font-bold">Telemetrie Monitor</span>
                                    </div>
                                    {editingUser.has_telemetry_access && <div className="w-2 h-2 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.8)]"></div>}
                                </div>
                                <p className="text-[10px] text-gray-500 mt-2">Erlaubt den Zugriff auf "Mission Control", auch ohne Admin-Rolle.</p>
                            </div>

                        </div>
                        <div className="flex justify-end gap-2 mt-8 pt-4 border-t border-gray-700">
                            <Button variant="ghost" onClick={() => setEditingUser(null)}>Abbrechen</Button>
                            <Button icon={Save} onClick={saveUser}>Speichern</Button>
                        </div>
                    </Card>
                </div>
            )}

            {/* LIST VIEW (Table) */}
            <div className="bg-[#1e1e24] border border-gray-800 rounded-lg overflow-hidden shadow-lg">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-gray-900 text-gray-400 uppercase text-xs font-bold tracking-wider">
                            <tr>
                                <th className="p-4 border-b border-gray-800 w-[30%]">User & Account</th>
                                <th className="p-4 border-b border-gray-800 w-[20%] text-center">Status Checks</th>
                                <th className="p-4 border-b border-gray-800 w-[20%]">Team & Rolle</th>
                                <th className="p-4 border-b border-gray-800 w-[15%]">Zuletzt online</th>
                                <th className="p-4 border-b border-gray-800 text-right w-[15%]">Aktionen</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800">
                            {sortedUsers.map(user => {
                                const anyUser = user as any; 
                                const isActiveUser = user.is_active !== false; 
                                const isPlaceholder = !!anyUser.is_placeholder;
                                const userAcceptedAt = user.rulebook_accepted_at ? new Date(user.rulebook_accepted_at).getTime() : 0;
                                const hasAcceptedRules = rulebookTimestamp > 0 && userAcceptedAt >= rulebookTimestamp;
                                const hasDiscord = !!anyUser.discord_id;
                                
                                const isAdmin = user.roles.includes(UserRole.ADMIN);
                                const isWebAdmin = user.roles.includes(UserRole.WEBADMIN);
                                const isSteward = user.roles.includes(UserRole.STEWARD);
                                
                                const teamName = anyUser.teamName;
                                const teamLogo = anyUser.teamLogo;
                                const isReserve = anyUser.is_reserve;
                                const hasTeam = !!anyUser.teamId;

                                return (
                                    <tr key={user.id} className={`hover:bg-gray-800/50 transition-colors ${!isActiveUser ? 'opacity-50 bg-black/20' : ''} ${isAdmin || isWebAdmin ? 'bg-f1-red/5' : ''}`}>
                                        
                                        {/* COL 1: USER INFO */}
                                        <td className="p-4">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-10 h-10 rounded bg-gray-800 flex items-center justify-center font-bold text-lg overflow-hidden border shrink-0 ${isAdmin ? 'border-f1-red text-f1-red' : isSteward ? 'border-yellow-500 text-yellow-500' : 'border-gray-700 text-gray-500'}`}>
                                                    {user.avatarUrl ? (
                                                        <img src={getImageUrl(user.avatarUrl)} className="w-full h-full object-cover" alt={user.username} />
                                                    ) : (
                                                        user.username.substring(0, 1).toUpperCase()
                                                    )}
                                                </div>
                                                <div className="min-w-0">
                                                    <div className={`font-bold truncate flex items-center gap-2 ${!isActiveUser ? 'text-gray-500 italic' : 'text-white'}`}>
                                                        {user.username}
                                                        {isAdmin && <Crown size={12} className="text-f1-red fill-current"/>}
                                                        {isPlaceholder && <Badge color="gray" className="text-[9px] py-0 px-1 opacity-60">PH</Badge>}
                                                    </div>
                                                    <div className="text-xs text-gray-500 truncate font-mono">
                                                        {user.eaId || <span className="italic opacity-50">Keine EA-ID</span>}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        
                                        {/* COL 2: STATUS INDICATORS */}
                                        <td className="p-4">
                                            <div className="flex items-center justify-center gap-2">
                                                <div title={hasDiscord ? "Discord Verknüpft" : "Keine Discord ID"} className={`p-1.5 rounded transition-colors ${hasDiscord ? 'text-[#5865F2] bg-[#5865F2]/10 border border-[#5865F2]/30' : 'text-gray-700 border border-gray-800'}`}>
                                                    <MessageCircle size={16} className={!hasDiscord ? "opacity-20" : ""}/>
                                                </div>

                                                {isActiveUser && rulebookTimestamp > 0 && (
                                                    <div title={hasAcceptedRules ? "Regelwerk akzeptiert" : "Regelwerk NICHT akzeptiert"} className={`p-1.5 rounded transition-colors ${hasAcceptedRules ? 'text-gray-700 opacity-20' : 'text-red-500 bg-red-900/10 border border-red-500/50 animate-pulse'}`}>
                                                        <BookOpen size={16} />
                                                    </div>
                                                )}

                                                {!isActiveUser && (
                                                    <Badge color="gray" className="flex items-center gap-1 border-gray-600 bg-gray-800 text-gray-400">
                                                        <Archive size={10}/> INAKTIV
                                                    </Badge>
                                                )}
                                            </div>
                                        </td>

                                        {/* COL 3: TEAM & ROLES */}
                                        <td className="p-4">
                                            <div className="space-y-1">
                                                <div className="flex items-center gap-2 min-h-[20px]">
                                                    {hasTeam ? (
                                                        <>
                                                            {teamLogo ? <img src={getImageUrl(teamLogo)} className="w-5 h-5 object-contain" alt="Team"/> : <Car size={16} className="text-gray-500"/>}
                                                            <span className="text-sm font-bold text-gray-300">{teamName}</span>
                                                            {isReserve && <span className="text-[9px] font-bold bg-yellow-500/20 text-yellow-500 px-1 rounded border border-yellow-500/30">RES</span>}
                                                        </>
                                                    ) : anyUser.is_reserve ? (
                                                        <span className="text-xs font-bold bg-gray-700 text-gray-300 px-2 py-0.5 rounded">POOL DRIVER</span>
                                                    ) : (
                                                        <span className="text-gray-600 text-xs">-</span>
                                                    )}
                                                </div>
                                                
                                                <div className="flex flex-wrap gap-1">
                                                    {user.roles.filter(r => r !== 'DRIVER').map(role => (
                                                        <span key={role} className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase border ${
                                                            role === 'WEBADMIN' ? 'bg-blue-900/30 text-blue-300 border-blue-800' :
                                                            role === 'ADMIN' ? 'bg-red-900/30 text-red-300 border-red-800' :
                                                            role === 'STEWARD' ? 'bg-yellow-900/30 text-yellow-300 border-yellow-800' :
                                                            'bg-gray-800 text-gray-400 border-gray-700'
                                                        }`}>
                                                            {getRoleLabel(role)}
                                                        </span>
                                                    ))}
                                                    {user.has_telemetry_access && !isWebAdmin && (
                                                        <span className="text-[9px] bg-indigo-900/30 text-indigo-300 px-1.5 py-0.5 rounded border border-indigo-800 font-bold">MC</span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>

                                        {/* COL 4: LAST LOGIN (NEW) */}
                                        <td className="p-4 text-xs">
                                            {anyUser.last_login_at ? (
                                                <div className="flex flex-col text-gray-300">
                                                    <span className="font-bold">{new Date(anyUser.last_login_at).toLocaleDateString()}</span>
                                                    <span className="text-gray-500">{new Date(anyUser.last_login_at).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</span>
                                                </div>
                                            ) : (
                                                <span className="text-gray-600 italic">Nie</span>
                                            )}
                                        </td>

                                        {/* COL 5: ACTIONS */}
                                        <td className="p-4 text-right">
                                            <div className="flex justify-end gap-1">
                                                {isPlaceholder && (
                                                    <button onClick={() => setMergingUser(user)} className="p-2 rounded bg-blue-900/20 text-blue-400 hover:text-white hover:bg-blue-800 border border-blue-900/30" title="Zusammenführen (Merge)">
                                                        <GitMerge size={14}/>
                                                    </button>
                                                )}
                                                <button onClick={() => handleToggleStatus(user)} className={`p-2 rounded transition-colors ${!isActiveUser ? 'bg-green-900/20 text-green-500 hover:bg-green-900/40 border border-green-800' : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'}`} title={!isActiveUser ? "Reaktivieren" : "Deaktivieren / Archivieren"}>
                                                    {!isActiveUser ? <UserCheck size={14}/> : <UserX size={14}/>}
                                                </button>
                                                <button onClick={() => handleResetPassword(user)} className="p-2 rounded bg-gray-800 text-gray-400 hover:text-yellow-500 hover:bg-yellow-900/20" title="Passwort zurücksetzen">
                                                    <Unlock size={14} />
                                                </button>
                                                <button onClick={() => setEditingUser(user)} className="p-2 rounded bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700" title="Bearbeiten">
                                                    <Edit3 size={14} />
                                                </button>
                                                <button onClick={() => handleDeleteUser(user)} className="p-2 rounded bg-gray-800 text-gray-400 hover:text-red-500 hover:bg-red-900/20" title="Löschen (Endgültig)">
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {filteredUsers.length === 0 && (
                    <div className="p-8 text-center text-gray-500 italic">Keine Benutzer gefunden.</div>
                )}
            </div>

            {/* Developer Tools Footer */}
            <div className="mt-8 pt-6 border-t border-gray-800 opacity-50 hover:opacity-100 transition-opacity">
                <div className="bg-gray-900/30 p-4 rounded border border-dashed border-gray-700 flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="flex items-center gap-3">
                        <TestTube size={18} className="text-gray-500"/>
                        <span className="text-xs text-gray-400 font-bold uppercase">Dev Sandbox</span>
                    </div>
                    <Button 
                        variant="ghost" 
                        onClick={handleSeedDrivers} 
                        disabled={seeding}
                        className="text-xs text-gray-500 hover:text-white"
                    >
                        {seeding ? <Loader2 className="animate-spin mr-2 w-3 h-3"/> : <Play className="mr-2 w-3 h-3"/>} 
                        Dummy Fahrer generieren
                    </Button>
                </div>
            </div>
        </div>
    );
};

const PointsEditor: React.FC<{
    title: string;
    points: number[];
    onChange: (newPoints: number[]) => void;
}> = ({ title, points, onChange }) => {
    const handleChange = (idx: number, val: string) => {
        const newArr = [...points];
        newArr[idx] = parseInt(val) || 0;
        onChange(newArr);
    };

    return (
        <div className="bg-gray-800/50 p-4 rounded border border-gray-700">
            <h4 className="font-bold text-sm uppercase text-white mb-3">{title}</h4>
            <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
                {Array.from({ length: 20 }).map((_, i) => {
                    const pts = points[i] !== undefined ? points[i] : 0;
                    return (
                        <div key={i} className="flex flex-col items-center">
                            <label className="text-[10px] text-gray-500 font-mono mb-1">P{i+1}</label>
                            <input 
                                type="number" 
                                className={`w-full text-center bg-gray-900 border rounded py-1 text-sm font-bold ${pts > 0 ? 'border-green-800 text-green-400' : 'border-gray-700 text-gray-600'}`}
                                value={pts}
                                onChange={e => handleChange(i, e.target.value)}
                            />
                        </div>
                    )
                })}
            </div>
        </div>
    );
};

const GlobalConfig: React.FC = () => {
    // ... (Implementation same as previous, adding new fields)
    const { user } = useAuth();
    const { settings: contextSettings, refreshSettings } = useLeague();
    const [settings, setSettings] = useState<any>({ ...contextSettings });
    const [saving, setSaving] = useState(false);
    const [testingDiscord, setTestingDiscord] = useState(false);
    const logoInputRef = useRef<HTMLInputElement>(null);
    const reserveInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if(contextSettings) setSettings(contextSettings);
    }, [contextSettings]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await apiRequest('settings.php', 'PUT', settings);
            await refreshSettings();
            alert("Einstellungen erfolgreich gespeichert.");
        } catch (e: any) {
            alert("Fehler beim Speichern: " + e.message);
        } finally {
            setSaving(false);
        }
    };

    const handleTestDiscord = async () => {
        if (!settings.discord_webhook_url) {
            alert("Bitte erst eine Webhook URL eintragen und speichern.");
            return;
        }
        setTestingDiscord(true);
        try {
            const res = await apiRequest<{message: string}>('test_discord.php', 'POST', {});
            alert(res.message);
        } catch(e: any) {
            alert("Test fehlgeschlagen: " + e.message);
        } finally {
            setTestingDiscord(false);
        }
    };

    const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setSettings({...settings, theme_color: val});
        document.documentElement.style.setProperty('--f1-red', val);
    };

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>, field: 'league_logo_url' | 'reserve_logo_url') => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setSettings({...settings, [field]: reader.result as string});
            };
            reader.readAsDataURL(file);
        }
    };
    
    const getUrl = (url?: string) => {
        if (!url) return null;
        if (url.startsWith('http') || url.startsWith('data:')) return url;
        const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
        return baseUrl ? `${baseUrl}/${url}` : url;
    };

    const getDbUpdateUrl = () => {
        const baseUrl = API_BASE_URL === '.' ? window.location.origin + window.location.pathname.replace(/\/+$/, '') : API_BASE_URL;
        return `${baseUrl}/php/update_db.php`;
    };

    const isWebAdmin = user?.roles.includes(UserRole.WEBADMIN);

    return (
        <Card title="Liga-Einstellungen">
            <div className="space-y-6">
                
                {/* Branding Section */}
                <div className="bg-gray-800/50 p-4 rounded border border-gray-700">
                    <h3 className="text-lg font-bold uppercase mb-4 text-white flex items-center gap-2">
                        <Palette size={18} className="text-f1-red"/> Design & Branding
                    </h3>
                    <div className="space-y-6">
                        <Input 
                            label="Liga-Name (wird im Header angezeigt)" 
                            value={settings.league_name} 
                            onChange={e => setSettings({...settings, league_name: e.target.value})}
                        />
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label className="block text-gray-400 text-sm font-bold mb-2 uppercase">Design-Farbe</label>
                                <div className="flex items-center gap-4">
                                    <input 
                                        type="color" 
                                        className="h-10 w-20 bg-transparent cursor-pointer rounded border border-gray-600"
                                        value={settings.theme_color}
                                        onChange={handleColorChange}
                                    />
                                    <div className="text-sm text-gray-400 font-mono">{settings.theme_color}</div>
                                </div>
                            </div>
                            
                            {/* Logo Uploads */}
                            <div>
                                <label className="block text-gray-400 text-sm font-bold mb-2 uppercase">Liga-Logo</label>
                                <div className="flex items-start gap-4">
                                    <div className="w-20 h-20 bg-gray-900 rounded border border-gray-700 flex items-center justify-center overflow-hidden relative group">
                                        {settings.league_logo_url ? (
                                            <img src={getUrl(settings.league_logo_url)!} alt="Preview" className="w-full h-full object-contain p-1" />
                                        ) : (
                                            <ImageIcon className="text-gray-600"/>
                                        )}
                                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity cursor-pointer" onClick={() => logoInputRef.current?.click()}>
                                            <Upload className="text-white w-6 h-6"/>
                                        </div>
                                    </div>
                                    <div>
                                        <input type="file" ref={logoInputRef} className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, 'league_logo_url')} />
                                        <Button size="sm" variant="secondary" onClick={() => logoInputRef.current?.click()}>Upload</Button>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label className="block text-gray-400 text-sm font-bold mb-2 uppercase flex items-center gap-2">Ersatzfahrer-Logo</label>
                                <div className="flex items-start gap-4">
                                    <div className="w-20 h-20 bg-gray-900 rounded border border-gray-700 flex items-center justify-center overflow-hidden relative group">
                                        {settings.reserve_logo_url ? (
                                            <img src={getUrl(settings.reserve_logo_url)!} alt="Reserve" className="w-full h-full object-contain p-1" />
                                        ) : (
                                            <Users className="text-gray-600"/>
                                        )}
                                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity cursor-pointer" onClick={() => reserveInputRef.current?.click()}>
                                            <Upload className="text-white w-6 h-6"/>
                                        </div>
                                    </div>
                                    <div>
                                        <input type="file" ref={reserveInputRef} className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, 'reserve_logo_url')} />
                                        <Button size="sm" variant="secondary" onClick={() => reserveInputRef.current?.click()}>Upload</Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Scoring System Section */}
                <div className="bg-gray-800/50 p-4 rounded border border-gray-700">
                    <h3 className="text-lg font-bold uppercase mb-4 text-white flex items-center gap-2">
                        <ListOrdered size={18} className="text-f1-red"/> Punktesysteme
                    </h3>
                    <div className="space-y-4">
                        <PointsEditor 
                            title="50% Renndistanz" 
                            points={settings.points_50 || []} 
                            onChange={(p) => setSettings({...settings, points_50: p})}
                        />
                        <PointsEditor 
                            title="100% Renndistanz" 
                            points={settings.points_100 || []} 
                            onChange={(p) => setSettings({...settings, points_100: p})}
                        />
                         <PointsEditor 
                            title="Sprint Rennen" 
                            points={settings.points_sprint || []} 
                            onChange={(p) => setSettings({...settings, points_sprint: p})}
                        />
                        <div className="grid grid-cols-2 gap-4 mt-2">
                            <Input 
                                label="Punkte für schnellste Runde" 
                                type="number" 
                                value={settings.point_fastest_lap} 
                                onChange={e => setSettings({...settings, point_fastest_lap: parseInt(e.target.value)})}
                            />
                            <Input 
                                label="Punkte für Driver of the Day" 
                                type="number" 
                                value={settings.point_dotd} 
                                onChange={e => setSettings({...settings, point_dotd: parseInt(e.target.value)})}
                            />
                        </div>
                    </div>
                </div>
                
                {/* PREDICTION GAME SETTINGS (NEW) */}
                <div className="bg-[#1a1a20] border border-gray-700 rounded-lg p-4">
                    <h3 className="text-lg font-bold uppercase mb-4 text-white flex items-center gap-2">
                        <Zap size={18} className="text-purple-500"/> Tippspiel Punkte
                    </h3>
                    <div className="grid grid-cols-3 gap-6">
                        <Input 
                            label="Exakter Treffer (Punkte)"
                            type="number"
                            value={settings.prediction_points_exact ?? 10}
                            onChange={e => setSettings({...settings, prediction_points_exact: parseInt(e.target.value)})}
                        />
                        <Input 
                            label="Fahrer in Top 10 (Punkte)"
                            type="number"
                            value={settings.prediction_points_top10 ?? 1}
                            onChange={e => setSettings({...settings, prediction_points_top10: parseInt(e.target.value)})}
                        />
                        <Input 
                            label="Bonus: Alles Richtig"
                            type="number"
                            value={settings.prediction_points_full_bonus ?? 25}
                            onChange={e => setSettings({...settings, prediction_points_full_bonus: parseInt(e.target.value)})}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-6 mt-4 pt-4 border-t border-gray-800">
                        <Input 
                            label="Sprint: Exakter Treffer"
                            type="number"
                            value={settings.prediction_points_sprint_exact ?? 5}
                            onChange={e => setSettings({...settings, prediction_points_sprint_exact: parseInt(e.target.value)})}
                        />
                        <Input 
                            label="Sprint: Fahrer in Top 3"
                            type="number"
                            value={settings.prediction_points_sprint_top3 ?? 1}
                            onChange={e => setSettings({...settings, prediction_points_sprint_top3: parseInt(e.target.value)})}
                        />
                    </div>
                </div>

                {/* Technical Settings */}
                <div className="bg-[#1a1a20] border border-gray-700 rounded-lg p-4">
                    <h3 className="text-lg font-bold uppercase mb-4 text-white flex items-center gap-2">
                        <MessageSquare size={18} className="text-f1-red"/> Social & Discord
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Input 
                            label="Twitch Kanal (z.B. 'f1esports')" 
                            value={settings.twitch_channel || ''} 
                            onChange={e => setSettings({...settings, twitch_channel: e.target.value})}
                        />
                        
                        <div>
                            <label className="block text-gray-400 text-sm font-bold mb-2 uppercase">Discord Webhook URL</label>
                            <div className="flex gap-2">
                                <input 
                                    className="w-full bg-gray-900 border border-gray-700 rounded py-2 px-3 text-gray-100 leading-tight focus:outline-none focus:border-f1-red transition-colors"
                                    placeholder="https://discord.com/api/webhooks/..."
                                    value={settings.discord_webhook_url || ''} 
                                    onChange={e => setSettings({...settings, discord_webhook_url: e.target.value})}
                                />
                                <Button 
                                    onClick={handleTestDiscord} 
                                    disabled={testingDiscord || !settings.discord_webhook_url} 
                                    variant="secondary"
                                    className="whitespace-nowrap px-4"
                                >
                                    {testingDiscord ? <Loader2 className="animate-spin w-4 h-4"/> : <Send size={16}/>}
                                </Button>
                            </div>
                            <p className="text-[10px] text-gray-500 mt-1">Für automatische Nachrichten (Anmeldung, Tickets, News).</p>
                        </div>
                    </div>
                </div>

                {/* Rules & Penalties */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-4 border-t border-gray-800">
                    <Input 
                        label="Einspruchsfrist (Stunden)" 
                        type="number" 
                        value={settings.protest_deadline_hours} 
                        onChange={e => setSettings({...settings, protest_deadline_hours: parseInt(e.target.value)})}
                    />
                    <Input 
                        label="Driver of the Day Zeitfenster (Std)" 
                        type="number" 
                        value={settings.dotd_window_hours} 
                        onChange={e => setSettings({...settings, dotd_window_hours: parseInt(e.target.value)})}
                    />
                    <Input 
                        label="Max. Strafpunkte (Sperre)" 
                        type="number" 
                        value={settings.penalty_points_ban} 
                        onChange={e => setSettings({...settings, penalty_points_ban: parseInt(e.target.value)})}
                    />
                    <Input 
                        label="Max. Fehlende Anmeldung" 
                        type="number" 
                        value={settings.attendance_strikes_limit} 
                        onChange={e => setSettings({...settings, attendance_strikes_limit: parseInt(e.target.value)})}
                    />
                </div>
                
                {/* Checkboxes */}
                <div className="flex flex-col justify-end pb-4 pt-2 gap-2">
                    <label className="flex items-center gap-2 cursor-pointer bg-gray-900 p-3 rounded border border-gray-700 hover:bg-gray-800 transition-colors">
                        <input 
                            type="checkbox"
                            className="w-5 h-5 bg-gray-800 border-gray-600 rounded text-f1-red focus:ring-0"
                            checked={settings.penalties_apply_to_team}
                            onChange={e => setSettings({...settings, penalties_apply_to_team: e.target.checked})}
                        />
                        <div>
                            <span className="text-sm font-bold text-gray-300 block">Fahrerstrafen zählen für Konstrukteure?</span>
                            <span className="text-[10px] text-gray-500 block">Wenn aktiv, werden Strafpunkte von Fahrern auch vom Team-Punktestand abgezogen.</span>
                        </div>
                    </label>
                    
                    <label className="flex items-center gap-2 cursor-pointer bg-gray-900 p-3 rounded border border-gray-700 hover:bg-gray-800 transition-colors">
                        <input 
                            type="checkbox"
                            className="w-5 h-5 bg-gray-800 border-gray-600 rounded text-f1-red focus:ring-0"
                            checked={settings.clean_race_counts_for_team}
                            onChange={e => setSettings({...settings, clean_race_counts_for_team: e.target.checked})}
                        />
                        <div>
                            <span className="text-sm font-bold text-gray-300 block">Clean Race Bonus (+1) zählt für Konstrukteure?</span>
                            <span className="text-[10px] text-gray-500 block">Wenn aktiv, erhalten Teams den Bonuspunkt für straffreies Fahren ebenfalls.</span>
                        </div>
                    </label>
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-800 gap-4">
                    {isWebAdmin && (
                        <a href={getDbUpdateUrl()} target="_blank" rel="noreferrer">
                            <Button variant="secondary" onClick={() => {}} className="bg-gray-700 hover:bg-gray-600 border border-gray-500">
                                <Database className="mr-2 w-4 h-4 text-yellow-500" /> Run DB Patch
                            </Button>
                        </a>
                    )}
                    <Button onClick={handleSave} disabled={saving}>
                        {saving ? <><Loader2 className="animate-spin mr-2"/> Speichern...</> : <><Save className="mr-2"/> Konfiguration speichern</>}
                    </Button>
                </div>
            </div>
        </Card>
    );
};

export const UserAdmin: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'users' | 'config'>('users');

    return (
        <div className="space-y-6 animate-fade-in pb-20">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold uppercase italic">Systemeinstellungen</h1>
                    <p className="text-gray-400 text-sm">Benutzer, Rollen & Konfiguration verwalten</p>
                </div>
                <div className="flex bg-gray-800 rounded p-1">
                    <button 
                        onClick={() => setActiveTab('users')}
                        className={`px-4 py-2 rounded text-sm font-bold uppercase flex items-center gap-2 transition-colors ${activeTab === 'users' ? 'bg-f1-red text-white' : 'text-gray-400 hover:text-white'}`}
                    >
                        <Users size={16}/> Benutzer
                    </button>
                    <button 
                        onClick={() => setActiveTab('config')}
                        className={`px-4 py-2 rounded text-sm font-bold uppercase flex items-center gap-2 transition-colors ${activeTab === 'config' ? 'bg-f1-red text-white' : 'text-gray-400 hover:text-white'}`}
                    >
                        <Settings size={16}/> Konfiguration
                    </button>
                </div>
            </div>

            {activeTab === 'users' ? <UserManager /> : <GlobalConfig />}
        </div>
    );
};
